import axios from 'axios';
import {
  buscarFilaEncomendasAvulsas,
  removerEncomendaAvulsaDaFila,
  salvarMapeamentoEncomendaAvulsaSync,
  salvarEncomendaAvulsaOffline
} from './offlineStorage';
import { RESPONSE_ERROR_HANDLER_POSTPONE_HEADER } from './responseUtil';
import { URL } from './ambienteActions';
import { getAuthentication } from '../security/securityContext';
import { isUserAuthenticated } from '../actions/util/authenticationJwtActions';

const SYNC_CONFIG = {
  headers: {
    [RESPONSE_ERROR_HANDLER_POSTPONE_HEADER]: true
  }
}

const MAX_TENTATIVAS_POLLING = 360; // 360 × 10s = 1 hora

let sincronizando = false;
const pollingEmAndamento = new Set();

export async function sincronizarEncomendasAvulsasOffline(dispatch) {
  if (sincronizando || !navigator.onLine || !isUserAuthenticated(getAuthentication())) return;

  let fila;
  try {
    fila = await buscarFilaEncomendasAvulsas();
  } catch (e) {
    return;
  }

  if (!fila || fila.length === 0) return;

  sincronizando = true;
  try {
    const fila = await buscarFilaEncomendasAvulsas();
    if (!fila || fila.length === 0) return;

    for (const encomenda of fila) {
      if (encomenda.erroSync) continue;
      if (encomenda.statusServidor === 'PROCESSANDO') {
        // timeout pode ter sido perdido em reload de página — reinicia polling, se não houver um já em andamento
        if (!pollingEmAndamento.has(encomenda.idChaveOffline)) {
          agendarPollingStatus(encomenda.idChaveOffline, encomenda, dispatch, 0);
        }
        continue;
      }
      await enviarEncomenda(encomenda, dispatch);
    }
  } catch (e) {
    // erro ao ler a fila local — próxima sincronização tenta de novo
  } finally {
    sincronizando = false;
  }
}

async function enviarEncomenda(encomenda, dispatch) {
  try {
    const response = await axios.post(`${URL}/encomendas-avulsas/encomenda-avulsa-offline`, {
      idChaveOffline: encomenda.idChaveOffline,
      dadosVenda: encomenda.dadosVenda
    }, SYNC_CONFIG);

    const { idChaveOffline } = response.data;

    await salvarEncomendaAvulsaOffline({ ...encomenda, statusServidor: 'PROCESSANDO' });

    agendarPollingStatus(idChaveOffline, encomenda, dispatch, 0);

  } catch (erro) {
    if (!erro.response) return;
    const status = erro.response.status;
    if (status === 401) return; // sessão expirada: tenta de novo após reautenticação
    if (status >= 400 && status < 500) {
      const violacoes = erro.response.data && erro.response.data.violacoes;
      const mensagemErro = violacoes && violacoes.length
        ? violacoes.map(v => v.mensagem).join(', ')
        : 'Erro HTTP ' + status;
      await salvarEncomendaAvulsaOffline({ ...encomenda, erroSync: mensagemErro, erroSyncStatus: status });
    }
  }
}

function agendarPollingStatus(idChaveOffline, encomenda, dispatch, tentativa) {
  if (tentativa === 0) {
    pollingEmAndamento.add(idChaveOffline);
  }

  if (tentativa >= MAX_TENTATIVAS_POLLING) {
    pollingEmAndamento.delete(idChaveOffline);
    salvarEncomendaAvulsaOffline({ ...encomenda, erroSync: 'Timeout aguardando processamento no servidor' });
    return;
  }

  setTimeout(async () => {
    try {
      const response = await axios.get(
        `${URL}/encomendas-avulsas/encomenda-avulsa-offline/${idChaveOffline}/status`,
        SYNC_CONFIG
      );
      const { status, encomendaAvulsaId, erroMensagem } = response.data;

      if (status === 'RESOLVIDO') {
        pollingEmAndamento.delete(idChaveOffline);
        salvarMapeamentoEncomendaAvulsaSync(idChaveOffline, encomendaAvulsaId);
        await removerEncomendaAvulsaDaFila(idChaveOffline);
        if (dispatch) dispatch({ type: 'ENCOMENDAS_AVULSAS_OFFLINE_ENVIADAS', payload: idChaveOffline });

      } else if (status === 'ERRO_SYNC') {
        pollingEmAndamento.delete(idChaveOffline);
        await salvarEncomendaAvulsaOffline({ ...encomenda, erroSync: erroMensagem || 'Erro no processamento' });

      } else {
        agendarPollingStatus(idChaveOffline, encomenda, dispatch, tentativa + 1);
      }
    } catch (erro) {
      if (erro.response && erro.response.status === 401) {
        pollingEmAndamento.delete(idChaveOffline);
        return; // sessão expirada: a próxima sincronização retoma o polling
      }
      agendarPollingStatus(idChaveOffline, encomenda, dispatch, tentativa + 1);
    }
  }, 10 * 1000);
}

export function inicializarEventosSyncOfflineEncomendaAvulsa(dispatch) {
  window.addEventListener('online', () => sincronizarEncomendasAvulsasOffline(dispatch));

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      sincronizarEncomendasAvulsasOffline(dispatch);
    }
  });

  window.addEventListener('pageshow', () => sincronizarEncomendasAvulsasOffline(dispatch));
  window.addEventListener('focus', () => sincronizarEncomendasAvulsasOffline(dispatch));

  setInterval(() => sincronizarEncomendasAvulsasOffline(dispatch), 30000);

  sincronizarEncomendasAvulsasOffline(dispatch);
}
